import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApps } from '@/contexts/AppsContext';
import { useAppId } from '@/hooks/useAppId';
import { graphApi, mindApi } from '@/services/api';
import { Loader2, Database, Network, Brain, Target } from 'lucide-react';
import GraphCanvas from './GraphCanvas';
import FilterPanel from './FilterPanel';
import NodeDetailPanel from './NodeDetailPanel';
import EdgeDetailPanel from './EdgeDetailPanel';
import ContextTab from './ContextTab';

const DATA_TABS = [
  { id: 'graph', label: 'Graph', icon: Network },
  { id: 'context', label: 'Context', icon: Brain },
];

const KNOWN_NODE_TYPES = [
  'Person', 'AbstractConcept', 'ContentAtom', 'HeartbeatInsight', 'Primitive', 'Tension',
];

export default function DataPage() {
  const appId = useAppId();
  const { currentApp, appReadiness } = useApps();

  const [activeTab, setActiveTab] = useState('graph');
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [nodeDetail, setNodeDetail] = useState(null);
  const [nodeDetailLoading, setNodeDetailLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [expanding, setExpanding] = useState(false);
  const [filters, setFilters] = useState({ nodeTypes: [...KNOWN_NODE_TYPES] });
  const [roots, setRoots] = useState([]);
  const [selectedRoot, setSelectedRoot] = useState(''); // '' = all

  // Extract dynamic entity types from graph data (types not in the known list)
  const entityTypes = useMemo(() => {
    const knownSet = new Set(KNOWN_NODE_TYPES);
    const types = new Set();
    for (const node of graphData.nodes) {
      if (node.nodeType && !knownSet.has(node.nodeType)) {
        types.add(node.nodeType);
      }
    }
    return [...types].sort();
  }, [graphData.nodes]);

  // Auto-add new entity types to the filter when they appear
  useEffect(() => {
    if (entityTypes.length === 0) return;
    setFilters(prev => {
      const missing = entityTypes.filter(t => !prev.nodeTypes.includes(t));
      if (missing.length === 0) return prev;
      return { ...prev, nodeTypes: [...prev.nodeTypes, ...missing] };
    });
  }, [entityTypes]);

  // Load root nodes for the selector
  useEffect(() => {
    if (!appId) return;
    mindApi.getRoots(appId)
      .then(res => {
        const data = res.data?.data || res.data || [];
        setRoots(Array.isArray(data) ? data : []);
      })
      .catch(() => setRoots([]));
  }, [appId]);

  // Load overview graph
  const loadOverview = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    setError(null);
    try {
      const params = selectedRoot ? { root_id: selectedRoot } : {};
      const res = await graphApi.getOverview(appId, params);
      const data = res.data?.data || res.data;
      setGraphData({ nodes: data.nodes || [], links: data.links || [] });
      setStats(data.stats || null);
      setExpandedNodes(new Set());
      setSelectedNode(null);
      setSelectedLink(null);
      setNodeDetail(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [appId, selectedRoot]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  // Fetch node detail when selectedNode changes
  useEffect(() => {
    if (!selectedNode || !appId) {
      setNodeDetail(null);
      return;
    }
    let cancelled = false;
    setNodeDetailLoading(true);
    graphApi.getNode(appId, selectedNode.id)
      .then(res => {
        if (!cancelled) setNodeDetail(res.data?.data || res.data);
      })
      .catch(() => {
        if (!cancelled) setNodeDetail(null);
      })
      .finally(() => {
        if (!cancelled) setNodeDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedNode, appId]);

  // Expand a node's neighbors
  const handleExpand = useCallback(
    async (nodeId) => {
      if (!appId || expandedNodes.has(nodeId)) return;
      setExpanding(true);
      try {
        const res = await graphApi.getNeighbors(appId, nodeId);
        const data = res.data?.data || res.data;
        const newNodes = data.nodes || [];
        const newLinks = data.links || [];

        setGraphData((prev) => {
          const existingIds = new Set(prev.nodes.map((n) => n.id));
          const mergedNodes = [
            ...prev.nodes,
            ...newNodes.filter((n) => !existingIds.has(n.id)),
          ];

          // Dedupe links by source+target+type
          const linkKey = (l) =>
            `${l.source?.id || l.source}|${l.target?.id || l.target}|${l.type}`;
          const existingLinks = new Set(prev.links.map(linkKey));
          const mergedLinks = [
            ...prev.links,
            ...newLinks.filter((l) => !existingLinks.has(linkKey(l))),
          ];

          return { nodes: mergedNodes, links: mergedLinks };
        });

        setExpandedNodes((prev) => new Set([...prev, nodeId]));
      } catch (err) {
        console.error('[DataPage] Expand failed:', err);
      } finally {
        setExpanding(false);
      }
    },
    [appId, expandedNodes]
  );

  // Search
  const handleSearch = useCallback(
    async ({ q, type }) => {
      if (!appId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await graphApi.search(appId, { q, type });
        const data = res.data?.data || res.data;
        setGraphData({ nodes: data.nodes || [], links: data.links || [] });
        setExpandedNodes(new Set());
        setSelectedNode(null);
        setSelectedLink(null);
        setNodeDetail(null);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    },
    [appId]
  );

  // Node click — clear selected link
  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    setSelectedLink(null);
  }, []);

  // Link click — clear selected node
  const handleLinkClick = useCallback((link) => {
    setSelectedLink(link);
    setSelectedNode(null);
    setNodeDetail(null);
  }, []);

  // Get edges for selected node
  const selectedEdges = selectedNode
    ? graphData.links.filter(
        (l) =>
          (l.source?.id || l.source) === selectedNode.id ||
          (l.target?.id || l.target) === selectedNode.id
      )
    : [];

  // Guards
  if (!currentApp) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <div className="text-center space-y-2">
          <p className="text-sm">Select an app to explore its graph.</p>
          <p className="text-xs">Use the app selector in the sidebar.</p>
        </div>
      </div>
    );
  }

  const readiness = appReadiness[currentApp.appId];
  if (readiness && !readiness.loading && !readiness.hasApiKey) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <div className="text-center space-y-3">
          <p className="text-sm font-medium text-foreground">App setup incomplete</p>
          <p className="text-xs">Complete app setup before exploring data.</p>
          <a href={`/apps/${currentApp.appId}`} className="text-sm text-primary hover:underline">
            Go to App Setup
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col -m-6" style={{ height: 'calc(100vh - 4rem)' }}>
      {/* Tab Bar + Root Selector */}
      <div className="flex items-center justify-between border-b bg-card/30 px-2 shrink-0">
        <div className="flex items-center">
          {DATA_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Root Node Scope Selector */}
        {activeTab === 'graph' && roots.length > 0 && (
          <div className="flex items-center gap-2 pr-2">
            <Target className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              value={selectedRoot}
              onChange={(e) => setSelectedRoot(e.target.value)}
              className="text-xs bg-background border rounded px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">All nodes</option>
              {roots.map((r) => (
                <option key={r.entityId} value={r.entityId}>
                  {r.properties?.name || r.entityId} ({r.type})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'graph' && (
        <div className="flex flex-1 min-h-0">
          {/* Filter Panel */}
          <FilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            onSearch={handleSearch}
            onReset={loadOverview}
            stats={stats}
            loading={loading}
            entityTypes={entityTypes}
          />

          {/* Graph Canvas */}
          <div className="flex-1 relative min-w-0">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Loading graph...</span>
                </div>
              </div>
            )}

            {error && (
              <div className="absolute top-3 left-3 right-3 z-10">
                <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-4 py-2">
                  {error}
                </div>
              </div>
            )}

            {!loading && graphData.nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-3 text-muted-foreground">
                  <Database className="h-10 w-10 mx-auto opacity-40" />
                  <p className="text-sm">No graph data yet.</p>
                  <p className="text-xs">Ingest some content to see nodes appear here.</p>
                </div>
              </div>
            )}

            <GraphCanvas
              data={graphData}
              onNodeClick={handleNodeClick}
              onLinkClick={handleLinkClick}
              selectedNodeId={selectedNode?.id}
              selectedLinkId={selectedLink?.id}
              filters={filters}
            />

            {/* Node count badge */}
            {graphData.nodes.length > 0 && (
              <div className="absolute bottom-3 left-3 text-xs text-muted-foreground bg-card/80 border rounded px-2 py-1">
                {graphData.nodes.length} nodes, {graphData.links.length} edges
              </div>
            )}
          </div>

          {/* Node Detail Panel */}
          {selectedNode && (
            <NodeDetailPanel
              node={selectedNode}
              nodeDetail={nodeDetail}
              nodeDetailLoading={nodeDetailLoading}
              edges={selectedEdges}
              onClose={() => { setSelectedNode(null); setNodeDetail(null); }}
              onExpand={handleExpand}
              isExpanded={expandedNodes.has(selectedNode.id)}
              expanding={expanding}
            />
          )}

          {/* Edge Detail Panel */}
          {selectedLink && (
            <EdgeDetailPanel
              link={selectedLink}
              onClose={() => setSelectedLink(null)}
            />
          )}
        </div>
      )}

      {activeTab === 'context' && (
        <div className="flex-1 min-h-0">
          <ContextTab />
        </div>
      )}
    </div>
  );
}
